import { useApiClient } from "@/hooks/useApiClient";
import { useFeatureFlags } from "@/configs/featureFlag";
import { useNavigate } from "react-router-dom";
import {
  type PictureResponse,
  type PictureTypeData,
  type AllMattersAnalyzeOptions,
} from "@/types/acquirebook";
import { type OwnerRelatedRegistrySelection } from "@/types/ownerRelatedRegistries";
import { toast } from "react-toastify";
import { HttpStatusCode, isAxiosError } from "axios";
import * as Sentry from "@sentry/react";
import { READ_ONLY_TOOLTIP_MESSAGE } from "@/utils/permissions";
import {
  makePictureRequestPayload,
  type ReusePictures,
} from "@/features/acquireBook/utils/pictureRequest";

export interface AcquireBookForm {
  handleSubmit: (
    chibanKaokuNumbers: string[],
    prefecture: string,
    city: string,
    locationWithoutCity: string,
    locationFull: string,
    pastDays: number | "",
    realEstateType: string,
    requestOptions: PictureTypeData,
    withOwnerAnalyze: boolean,
    withAllMattersAnalyze: boolean,
    identificationName: string,
    cancelLoading: () => void,
    setDisableConfirmModalCancelButton?: React.Dispatch<
      React.SetStateAction<boolean>
    >,
    reusePictures?: ReusePictures[],
    requestClosureBook?: boolean,
    ownerRelatedSelections?: OwnerRelatedRegistrySelection[],
    allMattersAnalyzeOptions?: AllMattersAnalyzeOptions
  ) => void;
}

export const useAcquireBookForm = (): AcquireBookForm => {
  const { apiClient } = useApiClient();
  const { acquiredRealEstateSearch } = useFeatureFlags();
  const navigate = useNavigate();

  const handleSubmit = (
    chibanKaokuNumbers: string[],
    prefecture: string,
    city: string,
    locationWithoutCity: string,
    locationFull: string,
    pastDays: number | "",
    realEstateType: string,
    requestOptions: PictureTypeData,
    withOwnerAnalyze: boolean,
    withAllMattersAnalyze: boolean,
    identificationName: string,
    cancelLoading: () => void,
    setDisableConfirmModalCancelButton?: React.Dispatch<
      React.SetStateAction<boolean>
    >,
    reusePictures: ReusePictures[] = [],
    requestClosureBook = false,
    ownerRelatedSelections: OwnerRelatedRegistrySelection[] = [],
    allMattersAnalyzeOptions?: AllMattersAnalyzeOptions
  ): void => {
    const req = makePictureRequestPayload({
      chibanKaokuNumbers,
      prefecture,
      city,
      locationWithoutCity,
      locationFull,
      pastDays,
      realEstateType,
      requestOptions,
      withOwnerAnalyze,
      withAllMattersAnalyze,
      identificationName,
      reusePictures,
      requestClosureBook,
      ownerRelatedSelections,
      allMattersAnalyzeOptions,
    });

    if (setDisableConfirmModalCancelButton) {
      setDisableConfirmModalCancelButton(true);
    }

    // 送信する
    apiClient
      .post<PictureResponse>("/picture-request/", req)
      .then((_res) => {
        navigate(
          acquiredRealEstateSearch === true
            ? "/acquirebooklist?view=requests"
            : "/acquirebooklist"
        );
      })
      .catch((e) => {
        if (
          isAxiosError(e) &&
          e.response?.status === HttpStatusCode.Forbidden
        ) {
          toast.error(READ_ONLY_TOOLTIP_MESSAGE);
          cancelLoading();
          return;
        }

        if (isAxiosError(e) && e.response && e.response.data) {
          /* eslint-disable @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment */
          if (e.response.data.nonFieldErrors?.length) {
            // エラー文字列が配列なので複数のエラー文字列が返されても表示できるようにする
            const errorList: React.ReactNode[] =
              e.response.data.nonFieldErrors.map(
                (value: string): React.ReactNode => {
                  return (
                    <>
                      {value}
                      <br />
                    </>
                  );
                }
              );
            toast.error(<>{errorList}</>);
          } else {
            toast.error(JSON.stringify(e.response.data));
          }
          /* eslint-enable @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment */
        }
        if (
          isAxiosError(e) &&
          (e.response?.status ?? HttpStatusCode.InternalServerError) >=
            HttpStatusCode.InternalServerError
        ) {
          Sentry.captureException(e);
        }
        console.error(e);
        cancelLoading();
      })
      .finally(() => {
        if (setDisableConfirmModalCancelButton) {
          setDisableConfirmModalCancelButton(false);
        }
      });
  };

  return {
    handleSubmit,
  };
};
